export type LoginOutcome = 'completed' | 'superseded';

type Params<TResult> = {
  /** Reads the session token the request is about to be sent under. */
  readSession: () => string;
  /** Performs the authentication request. */
  request: () => Promise<TResult>;
  /** Applies the result. Only reached when the session is still current. */
  commit: (result: TResult) => unknown;
};

/**
 * Runs an authentication request and commits its result only if the session it was
 * issued for is still the current one.
 *
 * An auth token is bound to the session that minted it, so a response that arrives
 * after the session was replaced (for example, a passkey prompt completes a login
 * first) is stale and must commit nothing. Returns 'superseded' so callers know not
 * to run success behavior such as redirecting.
 */
export async function commitLoginResultForSession<TResult>({
  readSession,
  request,
  commit,
}: Params<TResult>): Promise<LoginOutcome> {
  const sessionAtRequest = readSession();
  const result = await request();

  if (readSession() !== sessionAtRequest) {
    return 'superseded';
  }

  await commit(result);
  return 'completed';
}
